import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  alpha,
  Divider,
  Snackbar
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import axios from '../../utils/axiosConfig';
import { formatDate, printCallsheet } from './utils/callsheetHelpers';

/**
 * CallsheetHistory Component - Displays the history of callsheets for a project
 * @param {string} projectId - The ID of the project
 * @param {Function} loadCallsheet - Function to load a callsheet
 * @param {Function} resetCallsheet - Function to reset the callsheet to default
 * @param {Object} styles - Theme-aware styles
 * @returns {JSX.Element} - Rendered component
 */
const CallsheetHistory = ({ projectId, loadCallsheet, resetCallsheet, styles }) => {
  // Basic state
  const [callsheets, setCallsheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  
  // Enhanced error handling
  const [errorDetails, setErrorDetails] = useState({ message: '', action: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Filtering and sorting
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({ status: 'all' });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [callsheetToDelete, setCallsheetToDelete] = useState(null);
  
  // Preview functionality
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCallsheet, setPreviewCallsheet] = useState(null);
  
  // Status management
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedCallsheet, setSelectedCallsheet] = useState(null);
  
  // Action menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionCallsheet, setActionCallsheet] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchCallsheetHistory();
    }
  }, [projectId]);

  const fetchCallsheetHistory = async () => {
    setLoading(true);
    setErrorDetails({ message: '', action: null });
    setError(null);
    try {
      const response = await axios.get(`/api/v1/callsheets/project/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      setCallsheets(response.data);
      setSnackbar({
        open: true,
        message: 'Callsheet history loaded successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error fetching callsheet history:', err);
      const message = err.response?.data?.message || 'Failed to load callsheet history';
      setErrorDetails({
        message,
        action: () => fetchCallsheetHistory()
      });
      setError('Failed to load callsheet history. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sorting
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setPage(0); // Reset to first page when sorting changes
  };
  
  // Handle filtering
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setPage(0); // Reset to first page when filters change
  };
  
  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };
  
  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when search term changes
  };
  
  // Apply sorting, filtering, and search to callsheets
  const filteredAndSortedCallsheets = useMemo(() => {
    // First, filter by search term
    let result = callsheets.filter(callsheet => {
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        formatDate(callsheet.date).toLowerCase().includes(searchLower) ||
        String(callsheet.shootingDay || '').toLowerCase().includes(searchLower) ||
        (callsheet.status || '').toLowerCase().includes(searchLower) ||
        (callsheet.createdBy?.name || '').toLowerCase().includes(searchLower)
      );
    });
    
    // Then, apply filters
    if (filters.status && filters.status !== 'all') {
      result = result.filter(callsheet => callsheet.status === filters.status);
    }
    
    // Finally, sort
    result.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'shootingDay':
          aValue = Number(a.shootingDay || 0);
          bValue = Number(b.shootingDay || 0);
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'createdBy':
          aValue = a.createdBy?.name || '';
          bValue = b.createdBy?.name || '';
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt || a.date);
          bValue = new Date(b.updatedAt || b.date);
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return result;
  }, [callsheets, searchTerm, filters, sortConfig]);
  
  // Get current page of callsheets
  const paginatedCallsheets = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedCallsheets.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedCallsheets, page, rowsPerPage]);

  const handleLoadCallsheet = (callsheetId) => {
    loadCallsheet(callsheetId);
  };

  const handleCopyCallsheet = async (callsheetId) => {
    try {
      // First, get the callsheet data
      const response = await axios.get(`/api/v1/callsheets/${callsheetId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Create a new callsheet based on the existing one
      const callsheetData = response.data;
      
      // Update the date to today
      callsheetData.date = new Date();
      
      // Remove the _id field to create a new document
      delete callsheetData._id;
      
      // Save as a new callsheet
      await axios.post(`/api/v1/callsheets`, {
        ...callsheetData,
        projectId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Refresh the callsheet history
      fetchCallsheetHistory();
      
      // Load the new callsheet
      resetCallsheet();
    } catch (err) {
      console.error('Error copying callsheet:', err);
      setError('Failed to copy callsheet. Please try again.');
    }
  };

  // Delete confirmation dialog
  const openDeleteDialog = (callsheetId) => {
    setCallsheetToDelete(callsheetId);
    setDeleteDialogOpen(true);
  };
  
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCallsheetToDelete(null);
  };
  
  const handleDeleteCallsheet = async () => {
    if (!callsheetToDelete) return;
    
    try {
      await axios.delete(`/api/v1/callsheets/${callsheetToDelete}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Close dialog
      closeDeleteDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Callsheet deleted successfully',
        severity: 'success'
      });
      
      // Refresh the callsheet history
      fetchCallsheetHistory();
    } catch (err) {
      console.error('Error deleting callsheet:', err);
      setErrorDetails({
        message: err.response?.data?.message || 'Failed to delete callsheet',
        action: () => handleDeleteCallsheet()
      });
      setError('Failed to delete callsheet. Please try again.');
    }
  };

  const handleDownloadCallsheet = async (callsheetId) => {
    try {
      const response = await axios.get(`/api/v1/callsheets/${callsheetId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        },
        responseType: 'blob'
      });
      // Server provided a PDF blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `callsheet_${callsheetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'Callsheet downloaded successfully', severity: 'success' });
    } catch (err) {
      console.error('Error downloading callsheet:', err);
      // If endpoint is not implemented (404), gracefully fallback to client-side print-to-PDF
      if (err?.response?.status === 404) {
        try {
          const { data: cs } = await axios.get(`/api/v1/callsheets/${callsheetId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'x-auth-token': localStorage.getItem('token')
            }
          });
          // Open the callsheet in print view; user can "Save as PDF"
          printCallsheet(cs, 'legal', '0.2cm');
          setSnackbar({ open: true, message: 'Opened print view. Use "Save as PDF" to download.', severity: 'info' });
          return;
        } catch (fallbackErr) {
          console.error('Fallback print failed:', fallbackErr);
        }
      }
      setErrorDetails({
        message: err.response?.data?.message || 'Failed to download callsheet',
        action: () => handleDownloadCallsheet(callsheetId)
      });
      setError('Failed to download callsheet. Please try again.');
    }
  };
  
  // Preview functionality
  const handlePreview = async (callsheetId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/callsheets/${callsheetId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      setPreviewCallsheet(response.data);
      setPreviewOpen(true);
    } catch (err) {
      console.error('Error fetching callsheet preview:', err);
      setErrorDetails({
        message: err.response?.data?.message || 'Failed to load callsheet preview',
        action: () => handlePreview(callsheetId)
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewCallsheet(null);
  };
  
  // Status management
  const openStatusMenu = (event, callsheet) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedCallsheet(callsheet);
  };
  
  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setSelectedCallsheet(null);
  };
  
  const handleStatusChange = async (newStatus) => {
    if (!selectedCallsheet) return;
    
    try {
      await axios.patch(`/api/v1/callsheets/${selectedCallsheet._id}`, {
        status: newStatus
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Close menu
      closeStatusMenu();
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Callsheet status updated to ${newStatus}`,
        severity: 'success'
      });
      
      // Refresh the callsheet history
      fetchCallsheetHistory();
    } catch (err) {
      console.error('Error updating callsheet status:', err);
      setErrorDetails({
        message: err.response?.data?.message || 'Failed to update callsheet status',
        action: () => handleStatusChange(newStatus)
      });
    }
  };
  
  // Action menu
  const openActionMenu = (event, callsheet) => {
    setActionMenuAnchor(event.currentTarget);
    setActionCallsheet(callsheet);
  };
  
  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionCallsheet(null);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Check if a callsheet was recently updated (within last 24 hours)
  const isRecentlyUpdated = (date) => {
    const now = new Date();
    const updated = new Date(date);
    return (now - updated) < 24 * 60 * 60 * 1000; // 24 hours
  };

  if (loading && callsheets.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>      
      {/* Status change menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={closeStatusMenu}
      >
        <MenuItem sx={{ pointerEvents: 'none', opacity: 0.7 }}>
          <Typography variant="subtitle2">Change Status</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('Draft')}
          selected={selectedCallsheet?.status === 'Draft'}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Draft
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('Published')}
          selected={selectedCallsheet?.status === 'Published'}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PublishIcon fontSize="small" sx={{ mr: 1 }} />
            Published
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Action menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
      >
        <MenuItem onClick={() => {
          handleCopyCallsheet(actionCallsheet?._id);
          closeActionMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy
          </Box>
        </MenuItem>
        <MenuItem onClick={() => {
          openDeleteDialog(actionCallsheet?._id);
          closeActionMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.error.main }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this callsheet? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteCallsheet} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Callsheet Preview
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewCallsheet ? (
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Date</Typography>
                    <Typography variant="body1">{formatDate(previewCallsheet.date)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Shooting Day</Typography>
                    <Typography variant="body1">Day {previewCallsheet.shootingDay || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Status</Typography>
                    <Chip 
                      label={previewCallsheet.status || 'Draft'} 
                      size="small" 
                      color={previewCallsheet.status === 'Published' ? 'success' : 'default'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Created By</Typography>
                    <Typography variant="body1">{previewCallsheet.createdBy?.name || 'Unknown'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>General Crew Call</Typography>
                    <Typography variant="body1">{previewCallsheet.generalCall || 'Not set'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Weather</Typography>
                    <Typography variant="body1">{previewCallsheet.weather || 'Not specified'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Notes</Typography>
                    <Typography variant="body1">{
                      typeof previewCallsheet.notes === 'string'
                        ? previewCallsheet.notes
                        : (previewCallsheet.notes?.general || 'No notes')
                    }</Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button 
                  startIcon={<VisibilityIcon />} 
                  onClick={() => {
                    handleLoadCallsheet(previewCallsheet._id);
                    handleClosePreview();
                  }}
                >
                  View Full Callsheet
                </Button>
                <Button 
                  startIcon={<FileDownloadIcon />} 
                  onClick={() => handleDownloadCallsheet(previewCallsheet._id)}
                >
                  Download PDF
                </Button>
              </CardActions>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Paper 
        elevation={0} 
        sx={{ 
          ...(styles?.paper || {}),
          p: 4, 
          mb: 4,
          borderRadius: 1,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          backgroundColor: theme.palette.background.paper
        }}
      >
        {/* Header with title and search */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: '-0.25px',
                fontSize: '2rem',
                color: theme.palette.text.primary
              }}
            >
              <HistoryIcon 
                sx={{ 
                  mr: 2,
                  fontSize: '2rem',
                  color: 'primary.main'
                }} 
              />
              CALLSHEET HISTORY
            </Typography>
            <Chip 
              label={`${callsheets.length} Versions`} 
              color="secondary" 
              size="small" 
              variant="outlined"
              sx={{ ml: 2, alignSelf: 'flex-start' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Search field */}
            <TextField
              placeholder="Search callsheets..."
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ mr: 1, minWidth: '200px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Filter button */}
            <Tooltip title="Filter">
              <IconButton onClick={handleFilterMenuOpen}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            
            {/* Filter menu */}
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={handleFilterMenuClose}
            >
              <MenuItem sx={{ pointerEvents: 'none', opacity: 0.7 }}>
                <Typography variant="subtitle2">Filter by Status</Typography>
              </MenuItem>
              <MenuItem 
                selected={filters.status === 'all'}
                onClick={() => {
                  handleFilterChange('status', 'all');
                  handleFilterMenuClose();
                }}
              >
                All
              </MenuItem>
              <MenuItem 
                selected={filters.status === 'Draft'}
                onClick={() => {
                  handleFilterChange('status', 'Draft');
                  handleFilterMenuClose();
                }}
              >
                Draft
              </MenuItem>
              <MenuItem 
                selected={filters.status === 'Published'}
                onClick={() => {
                  handleFilterChange('status', 'Published');
                  handleFilterMenuClose();
                }}
              >
                Published
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        
        {/* Error alerts */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={errorDetails.action && (
              <Button color="inherit" size="small" onClick={errorDetails.action}>
                Retry
              </Button>
            )}
          >
            {errorDetails.message || error}
          </Alert>
        )}
        
        {/* Active filters display */}
        {filters.status !== 'all' && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>Active filters:</Typography>
            <Chip 
              label={`Status: ${filters.status}`} 
              size="small" 
              onDelete={() => handleFilterChange('status', 'all')}
              sx={{ mr: 1 }}
            />
          </Box>
        )}
        
        {filteredAndSortedCallsheets.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
            {searchTerm || filters.status !== 'all' 
              ? 'No callsheets match your search criteria.'
              : 'No callsheets found for this project. Create your first callsheet!'}
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ position: 'relative' }}>
              {loading && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'date'}
                        direction={sortConfig.key === 'date' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('date')}
                      >
                        <Typography variant="subtitle2">Date</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'shootingDay'}
                        direction={sortConfig.key === 'shootingDay' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('shootingDay')}
                      >
                        <Typography variant="subtitle2">Shooting Day</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'status'}
                        direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        <Typography variant="subtitle2">Status</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'createdBy'}
                        direction={sortConfig.key === 'createdBy' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('createdBy')}
                      >
                        <Typography variant="subtitle2">Created By</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'updatedAt'}
                        direction={sortConfig.key === 'updatedAt' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('updatedAt')}
                      >
                        <Typography variant="subtitle2">Last Updated</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center"><Typography variant="subtitle2">Actions</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCallsheets.map((callsheet) => (
                    <TableRow 
                      key={callsheet._id} 
                      hover
                      sx={{
                        backgroundColor: isRecentlyUpdated(callsheet.updatedAt || callsheet.date) ? 
                          alpha(theme.palette.info.light, 0.1) : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(callsheet.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Day {callsheet.shootingDay || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={callsheet.status || 'Draft'} 
                          size="small" 
                          color={callsheet.status === 'Published' ? 'success' : 'default'}
                          sx={{ 
                            fontWeight: callsheet.status === 'Published' ? 'bold' : 'normal',
                            cursor: 'pointer',
                            ...styles?.chip
                          }}
                          onClick={(e) => openStatusMenu(e, callsheet)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {callsheet.createdBy?.name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(callsheet.updatedAt || callsheet.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="View/Edit">
                            <IconButton 
                              size="small" 
                              onClick={() => handleLoadCallsheet(callsheet._id)}
                              sx={styles?.iconButton}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDownloadCallsheet(callsheet._id)}
                              sx={styles?.iconButton}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton 
                              size="small" 
                              onClick={(e) => openActionMenu(e, callsheet)}
                              sx={styles?.iconButton}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAndSortedCallsheets.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CallsheetHistory;
